import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardDescription, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { CreditCard, Sparkles, Lock, ArrowRight } from "lucide-react";
import { api } from "../../services/api";
import { useProfile } from "../../contexts/ProfileContext";
import { useToast } from "../../contexts/ToastContext";
import { Loader2 } from "lucide-react";
import Logo from "../dashboard/Logo";
interface NotSubscribedProps {
  featureName?: string;
}

export function NotSubscribed(_props: NotSubscribedProps) {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useProfile();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [trialEligible, setTrialEligible] = useState<boolean | null>(null);
  const [checkingTrial, setCheckingTrial] = useState(true);
  const [trialEndedDate, setTrialEndedDate] = useState<string | null>(null);
  const [trialDuration, setTrialDuration] = useState(5);

  useEffect(() => {
    const checkTrialEligibility = async () => {
      try {
        setCheckingTrial(true);
        
        // Fetch ONLY trial status (fast DB query) to avoid slow payment gateway query (getSubscriptionStatus)
        const trialStatus = await api.getTrialStatus().catch(() => ({ eligible: false, durationDays: 5 }));

        setTrialEligible(trialStatus.eligible);
        if (trialStatus.durationDays) setTrialDuration(trialStatus.durationDays);

        // Process subscription status to detect ended trial date from local profile instead of slow backend call
        try {
          if (profile) {
            const endDateStr = (profile as any).subscriptionEndDate || (profile as any).subscription_end_date;
            if (endDateStr) {
              const end = new Date(endDateStr);
              const now = new Date();
              
              // If subscription is expired or it was a trial that has ended
              if (end <= now && profile.subscriptionStatus !== 'active') {
                setTrialEndedDate(end.toLocaleDateString('id-ID'));
              } else {
                setTrialEndedDate(null);
              }
            } else {
              setTrialEndedDate(null);
            }
          }
        } catch (_) {
          setTrialEndedDate(null);
        }
      } catch (error) {
        console.error("Failed to check trial eligibility:", error);
        setTrialEligible(false);
      } finally {
        setCheckingTrial(false);
      }
    };

    // Only check if user is Free plan and not admin/developer
    if (profile?.subscriptionPlan === "Free" && profile?.role === "user") {
      checkTrialEligibility();
    } else {
      setTrialEligible(false);
      setCheckingTrial(false);
    }
  }, [profile]);

  const handleStartTrial = async () => {
    try {
      setIsLoading(true);
      await api.startTrial();

      // Show toast first
      showToast({
        type: "success",
        title: "Free Trial Started! 🎉",
        message: `You now have ${trialDuration} days of Pro features. Enjoy!`,
      });

      // Wait a bit for toast to show, then refresh profile
      setTimeout(async () => {
        try {
          console.log('Refreshing profile after trial start...');
          await refreshProfile(true); // Force refresh profile

          // Wait a bit longer to ensure profile state is updated
          setTimeout(() => {
            console.log('Profile refreshed, navigating to dashboard...');
            // Navigate to dashboard to see unlocked features
            navigate('/dashboard');
            // Force a page refresh after navigation to ensure state is synced
            setTimeout(() => {
              window.location.reload();
            }, 100);
          }, 500);
        } catch (refreshError) {
          console.error('Error refreshing profile:', refreshError);
          // Still navigate even if refresh fails, but reload to sync state
          navigate('/dashboard');
          setTimeout(() => {
            window.location.reload();
          }, 100);
        }
      }, 1000); // Increased delay to let toast show longer
    } catch (error: any) {
      console.error("Failed to start trial:", error);
      showToast({
        type: "error",
        title: "Failed to Start Trial",
        message: error.message || "Something went wrong. Please try again.",
      });
      setIsLoading(false);
    }
  };

  const handleSubscribe = () => {
    navigate("/subscription");
  };

  // Don't show for admin/developer
  if (profile?.role === "admin" || profile?.role === "developer") {
    return null;
  }

  if (checkingTrial) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] pt-24 pb-12 px-4 md:px-8">
      <div className="grid lg:grid-cols-5 gap-6 w-full max-w-5xl mx-auto my-auto items-stretch">
        {/* Left Card: Lock and Actions */}
        <Card className="lg:col-span-2 flex flex-col justify-center text-center p-6 shadow-lg">
          <div className="mx-auto w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-6">
            <Lock className="w-10 h-10 text-muted-foreground" />
          </div>
          <CardTitle className="text-2xl mb-8 flex flex-wrap items-center justify-center gap-2">
            <span>Welcome to</span>
            <Logo 
              showText 
              className="items-center gap-0" 
              badgeClassName="h-7 w-9 transform -translate-y-[1px]" 
              textClassName="text-2xl font-bold tracking-tight text-foreground dark:text-white" 
            />
          </CardTitle>
          {!checkingTrial && trialEndedDate && (
            <div className="mb-6 text-sm text-muted-foreground bg-muted/50 p-3 rounded-md animate-in fade-in zoom-in duration-300">
              Your free trial ended on <span className="font-medium">{trialEndedDate}</span>. Subscribe now to regain access.
            </div>
          )}

          <div className="flex flex-col gap-3 w-full mt-auto lg:mt-0">
            <CardDescription className="text-base mb-1">
              {/* Start your free trial here to access {featureName}. */}
              Click here to access free trial
            </CardDescription>
            
            {trialEligible === true && (
              <Button
                onClick={handleStartTrial}
                disabled={isLoading}
                size="lg"
                className="w-full h-14 text-sm md:text-base bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white animate-in fade-in slide-in-from-bottom-2 duration-300"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Start Free Trial ({trialDuration} Days)
                  </>
                )}
              </Button>
            )}

            <Button
              onClick={handleSubscribe}
              variant={trialEligible === true ? "outline" : "default"}
              size="lg"
              className={`w-full h-14 text-sm md:text-base ${trialEligible !== true ? "bg-primary text-primary-foreground" : ""} animate-in fade-in duration-300`}
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {trialEligible === true ? "Or Subscribe Now" : "Subscribe Now"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {!checkingTrial && trialEligible === true && (
            <p className="mt-4 text-xs text-muted-foreground animate-in fade-in duration-300">
              Free trial includes full Pro access. No credit card required.
            </p>
          )}
        </Card>

        {/* Right Card: Features List */}
        <Card className="lg:col-span-3 bg-muted/10 p-6 flex flex-col shadow-lg border-white/5">
          <h3 className="font-semibold text-lg mb-6 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Premium Features Available
          </h3>
          <div className="overflow-hidden rounded-lg border border-border/50 bg-background/50">
            <table className="w-full text-sm text-left">
              <thead className="bg-muted/50">
                <tr className="border-b border-border/50">
                  <th className="py-3 px-4 font-semibold text-foreground/80 w-1/3">Feature</th>
                  <th className="py-3 px-4 font-semibold text-foreground/80">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50 text-muted-foreground">
                <tr className="hover:bg-muted/20 transition-colors">
                  <td className="py-3 px-4 font-medium text-foreground">Watchlist</td>
                  <td className="py-3 px-4">Track live movements, momentum, and key levels seamlessly in one unified dashboard</td>
                </tr>
                <tr className="hover:bg-muted/20 transition-colors">
                  <td className="py-3 px-4 font-medium text-foreground">Market Analysis</td>
                  <td className="py-3 px-4">Relative Rotation Graph (RRG), Seasonality, Trend Filter, and Accumulation Distribution</td>
                </tr>
                <tr className="hover:bg-muted/20 transition-colors">
                  <td className="py-3 px-4 font-medium text-foreground">Broker Analysis</td>
                  <td className="py-3 px-4">Broker Summary, Broker Transaction, and Broker Inventory</td>
                </tr>
                <tr className="hover:bg-muted/20 transition-colors">
                  <td className="py-3 px-4 font-medium text-foreground">Stock Analysis</td>
                  <td className="py-3 px-4">Trade Footprints, detailed transaction analysis, and Bid-Offer Replay</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

